import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router';
import { useUpdateCustomSlugMutation, useRemoveCustomSlugMutation } from 'store/services/user/meApi';
import { useDebouncedSlugCheck, validateSlugFormat } from 'hooks/useDebouncedSlugCheck';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import './CustomSlugInput.css';

interface CustomSlugInputProps {
  currentSlug: string | null | undefined;
  customSlugSetAt: string | null | undefined;
  isEliteActive: boolean;
}

const COOLDOWN_DAYS = 30;
const BASE_URL = 'trademagic.gg/u/';

function getCooldownEnd(slugSetAt: string | null | undefined): Date | null {
  if (!slugSetAt) return null;
  const setDate = new Date(slugSetAt);
  const cooldownEnd = new Date(setDate.getTime() + COOLDOWN_DAYS * 24 * 60 * 60 * 1000);
  return cooldownEnd > new Date() ? cooldownEnd : null;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const CustomSlugInput: React.FC<CustomSlugInputProps> = ({
  currentSlug,
  customSlugSetAt,
  isEliteActive,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const [updateSlug, { isLoading: isSaving }] = useUpdateCustomSlugMutation();
  const [removeSlug, { isLoading: isRemoving }] = useRemoveCustomSlugMutation();

  const { status, message } = useDebouncedSlugCheck(inputValue, currentSlug ?? null);

  const cooldownEnd = getCooldownEnd(customSlugSetAt);
  const isOnCooldown = !!cooldownEnd;

  const handleCopy = useCallback(async () => {
    const url = `https://${BASE_URL}${currentSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Profile link copied!');
    } catch {
      toast.error('Failed to copy link');
    }
  }, [currentSlug]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setInputValue(value.slice(0, 30));
    setShowSuccess(false);
  };

  const handleStartEdit = () => {
    setInputValue(currentSlug || '');
    setIsEditing(true);
    setShowSuccess(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInputValue('');
    setShowSuccess(false);
  };

  const handleSave = async () => {
    const validation = validateSlugFormat(inputValue);
    if (!validation.valid || status !== 'available') return;

    try {
      await updateSlug({ slug: inputValue }).unwrap();
      setIsEditing(false);
      setShowSuccess(true);
      toast.success('Custom URL saved!');
    } catch (err) {
      const fetchErr = err as FetchBaseQueryError;
      const status = fetchErr?.status;
      if (status === 409) {
        toast.error('This URL is not available');
      } else if (status === 429) {
        const data = fetchErr?.data as { message?: string } | undefined;
        toast.error(data?.message || 'Please wait before changing your URL again');
      } else if (status === 403) {
        toast.error('Elite subscription required');
      } else {
        toast.error('Failed to save custom URL');
      }
    }
  };

  const handleRemove = async () => {
    try {
      await removeSlug().unwrap();
      setIsEditing(false);
      setShowSuccess(false);
      toast.success('Custom URL removed');
    } catch {
      toast.error('Failed to remove custom URL');
    }
  };

  const canSave = status === 'available' && inputValue.length >= 3 && !isSaving;

  // Non-Elite with existing slug (lapsed) — read-only
  if (!isEliteActive && currentSlug) {
    return (
      <div className="custom-slug-container">
        <div className="custom-slug-current">
          <span className="custom-slug-current-url">{BASE_URL}{currentSlug}</span>
          <button className="custom-slug-copy-btn" onClick={handleCopy}>Copy</button>
        </div>
        <p className="custom-slug-lapsed-notice">
          <Link to="/elite">Resubscribe to Elite</Link> to change your URL.
        </p>
      </div>
    );
  }

  // Non-Elite without slug — shouldn't normally render this component, but handle gracefully
  if (!isEliteActive) {
    return null;
  }

  // Elite user — show current slug or editing state
  return (
    <div className="custom-slug-container">
      {/* Display current slug when not editing */}
      {currentSlug && !isEditing && (
        <>
          <div className="custom-slug-current">
            <span className="custom-slug-current-url">{BASE_URL}{currentSlug}</span>
            <button className="custom-slug-copy-btn" onClick={handleCopy}>Copy</button>
            {!isOnCooldown && (
              <button className="custom-slug-edit-btn" onClick={handleStartEdit}>Edit</button>
            )}
          </div>
          {isOnCooldown && cooldownEnd && (
            <p className="custom-slug-cooldown">
              You can change your URL after {formatDate(cooldownEnd)}
            </p>
          )}
        </>
      )}

      {/* Show success message after saving */}
      {showSuccess && !isEditing && currentSlug && (
        <div className="custom-slug-success">
          &#10003; Your custom URL is live!
        </div>
      )}

      {/* Editing / initial claim state */}
      {(isEditing || !currentSlug) && (
        <>
          <div className={`custom-slug-input-row slug-status-${status}`}>
            <span className="custom-slug-prefix">{BASE_URL}</span>
            <input
              className="custom-slug-input"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="your-custom-url"
              maxLength={30}
              autoFocus
            />
          </div>

          {inputValue.length > 0 && (
            <div className="custom-slug-char-count">
              {inputValue.length}/30
            </div>
          )}

          {status !== 'idle' && (
            <div className={`custom-slug-status status-${status}`}>
              {status === 'validating' && (
                <><span className="custom-slug-spinner" /> Checking availability...</>
              )}
              {status === 'available' && <><span>&#10003;</span> {message}</>}
              {status === 'unavailable' && <><span>&#10007;</span> {message}</>}
              {status === 'invalid' && <><span>&#10007;</span> {message}</>}
            </div>
          )}

          <div className="custom-slug-actions">
            <button
              className="custom-slug-save-btn"
              onClick={handleSave}
              disabled={!canSave}
            >
              {isSaving ? 'Saving...' : (currentSlug ? 'Update URL' : 'Claim URL')}
            </button>
            {currentSlug && isEditing && (
              <button className="custom-slug-cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
            )}
            {currentSlug && isEditing && (
              <button
                className="custom-slug-cancel-btn"
                onClick={handleRemove}
                disabled={isRemoving}
                style={{ color: '#f44336', borderColor: 'rgba(244, 67, 54, 0.3)' }}
              >
                {isRemoving ? 'Removing...' : 'Remove'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomSlugInput;
